import WbSunnyIcon from "@mui/icons-material/WbSunny";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WeekendIcon from "@mui/icons-material/Weekend";
import CelebrationIcon from "@mui/icons-material/Celebration";
import moment from "moment";

// Shared tokens + shift-type classification for the redesigned Shift screen, in
// one place so the calendar chips, legend and summary panel stay in sync.
export const shiftPalette = {
  pageBg: "#F6F8FB",
  card: "#FFFFFF",
  border: "#ECEEF2",
  text: "#101828",
  subText: "#667085",
  green: "#12B76A",
  purple: "#7A5AF8",
  blue: "#2E90FA",
  gray: "#98A2B3",
  amber: "#F79009",
};

export const shiftCardSx = {
  borderRadius: 3,
  border: `1px solid ${shiftPalette.border}`,
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  bgcolor: shiftPalette.card,
};

// Category metadata — the single source for a type's colour, chip icon and
// display label. `key` doubles as the summary-count bucket.
export const SHIFT_CATEGORIES = {
  general: { key: "general", label: "General Shift", color: shiftPalette.green, icon: WbSunnyIcon },
  night: { key: "night", label: "Night Shift", color: shiftPalette.purple, icon: DarkModeIcon },
  flexible: { key: "flexible", label: "Flexible Shift", color: shiftPalette.blue, icon: ScheduleIcon },
  weeklyOff: { key: "weeklyOff", label: "Week Off", color: shiftPalette.gray, icon: WeekendIcon },
  holiday: { key: "holiday", label: "Holiday", color: shiftPalette.amber, icon: CelebrationIcon },
};

// Legend order (matches image 2's footer legend).
export const LEGEND = [
  SHIFT_CATEGORIES.general,
  SHIFT_CATEGORIES.night,
  SHIFT_CATEGORIES.flexible,
  SHIFT_CATEGORIES.weeklyOff,
  SHIFT_CATEGORIES.holiday,
];

// Map a shift row to a category from its name + weekoff flag. Returns null when
// there's no shift for the day (adjacent-month padding / "not joined"), so the
// caller can render an empty cell.
export const classifyShift = (shiftName, isWeekoff) => {
  const n = (shiftName || "").trim().toLowerCase();
  if (isWeekoff || n === "weekoff" || n.includes("week off") || n.includes("weekly off")) {
    return SHIFT_CATEGORIES.weeklyOff;
  }
  if (n.includes("holiday")) return SHIFT_CATEGORIES.holiday;
  if (n === "" || n === "-" || n.includes("not joined")) return null;
  if (n.includes("night")) return SHIFT_CATEGORIES.night;
  if (n.includes("flexible") || n.includes("flexi")) return SHIFT_CATEGORIES.flexible;
  return SHIFT_CATEGORIES.general;
};

export const formatShiftClock = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["HH:mm:ss", "HH:mm", "H:mm:ss", "H:mm"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : value;
};

// Tally a list of raw shift rows (from daily_user_shift_assignments) into the
// summary buckets shown on the right panel. Total Assigned counts working
// shifts only (general + night + flexible), excluding offs and holidays.
export const summarizeShifts = (rows) => {
  const counts = { general: 0, night: 0, flexible: 0, weeklyOff: 0, holiday: 0 };
  (rows || []).forEach((r) => {
    const cat = classifyShift(r?.shift_name, r?.is_weekoff);
    if (cat) counts[cat.key] += 1;
  });
  return {
    ...counts,
    totalAssigned: counts.general + counts.night + counts.flexible,
  };
};
